import {
  Shield,
  BadgeCheck,
  Banknote,
  Snowflake,
  Layers,
  Wind,
} from "lucide-react";
import { AreaPageShell } from "./AreaPageShell";

export interface EavestroughFaq {
  question: string;
  answer: string;
}

export interface EavestroughPageData {
  city: string;
  citySlug: string;
  serviceName: string;
  title: string;
  description: string;
  h1: string;
  heroIntro: string;
  climateText: string;
  localPresenceText: string;
  warrantyText: string;
  pricingText: string;
  priceFactorsText: string;
  financingText: string;
  step1: string;
  step2: string;
  step3: string;
  step4: string;
  profileText: string;
  materialsText: string;
  leafGuardText: string;
  faqs: EavestroughFaq[];
}

const buildSchema = (data: EavestroughPageData) => {
  const base = "https://www.saskroofingcompany.com";
  const url = `${base}/areas/${data.citySlug}/eavestrough`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor",
    "@id": `${base}/#localbusiness`,
    name: "Sask Roofing Company",
    url: `${base}/`,
    telephone: "+1-639-739-7969",
    email: "info@saskroofingcompany.com",
    areaServed: {
      "@type": "City",
      name: data.city,
      containedInPlace: { "@type": "State", name: "Saskatchewan" },
    },
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${url}#service`,
    serviceType: "Eavestrough Installation",
    name: "Eavestrough",
    description: data.description,
    url,
    provider: {
      "@id": `${base}/#localbusiness`,
      "@type": "RoofingContractor",
      name: "Sask Roofing Company",
      url: `${base}/`,
      telephone: "+1-639-739-7969",
    },
    areaServed: { "@type": "City", name: data.city },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "CAD",
      lowPrice: "1200",
      highPrice: "2500",
    },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `${base}/contact`,
      servicePhone: {
        "@type": "ContactPoint",
        telephone: "+1-639-739-7969",
        contactType: "customer service",
        areaServed: "CA-SK",
        availableLanguage: "English",
      },
    },
    termsOfService: `${base}/about#warranty`,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "87",
      bestRating: "5",
      worstRating: "1",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${base}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: "Service Areas",
        item: `${base}/areas`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.city,
        item: `${base}/areas/${data.citySlug}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Eavestrough",
        item: url,
      },
    ],
  };

  return [breadcrumbSchema, localBusinessSchema, serviceSchema, faqSchema];
};

export function EavestroughPage({ data }: { data: EavestroughPageData }) {
  return (
    <AreaPageShell
      data={{
        ...data,
        schema: buildSchema(data),
        canonical: `/areas/${data.citySlug}/eavestrough`,
      }}
      heroImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/67bfca73-3cfe-4fd0-b9fd-9ed4f4780799.png&output=webp&q=60"
      heroAlt={`Eavestrough installation in ${data.city}`}
      whyBlocks={[
        {
          icon: Snowflake,
          title: `Ice damming & eavestrough in ${data.city}`,
          text: data.climateText,
        },
        {
          icon: Shield,
          title: `Local eavestrough installation crews in ${data.city}`,
          text: data.localPresenceText,
        },
        {
          icon: BadgeCheck,
          title: `Eavestrough warranties in ${data.city}`,
          text: data.warrantyText,
        },
      ]}
      costBlocks={[
        {
          title: `Eavestrough price range in ${data.city}`,
          text: data.pricingText,
        },
        {
          title: `What affects eavestrough cost in ${data.city}`,
          text: data.priceFactorsText,
        },
        {
          title: `Eavestrough financing & payment in ${data.city}`,
          text: data.financingText,
        },
      ]}
      extraCostTiles={[
        {
          label: "Eavestrough",
          value: "$1,200–$2,500",
          note: "average Saskatchewan home",
        },
        {
          label: "Profiles",
          value: '5" & 6"',
          note: "seamless aluminum",
        },
        {
          label: "Forming",
          value: "On-site",
          note: "no seams, no leaks",
        },
      ]}
      extraCallout={{
        icon: Banknote,
        title: `Seamless eavestrough means fewer leaks in ${data.city}`,
        text: "We form seamless aluminum eavestrough on-site to the exact length of your home. No seams means fewer joints, fewer leaks, and less ice-dam damage over Saskatchewan winters.",
      }}
      processSteps={[
        {
          title: `Free eavestrough measurement & estimate in ${data.city}`,
          text: data.step1,
        },
        {
          title: `On-site seamless eavestrough forming in ${data.city}`,
          text: data.step2,
        },
        {
          title: `Eavestrough install & downspout placement in ${data.city}`,
          text: data.step3,
        },
        {
          title: `Eavestrough final walkthrough & cleanup in ${data.city}`,
          text: data.step4,
        },
      ]}
      materialBlocks={[
        {
          icon: Layers,
          title: `5" vs 6" eavestrough profiles in ${data.city}`,
          text: data.profileText,
        },
        {
          icon: Shield,
          title: `Aluminum vs steel eavestrough in ${data.city}`,
          text: data.materialsText,
        },
        {
          icon: Wind,
          title: `Leaf guard options for ${data.city} homes`,
          text: data.leafGuardText,
        },
      ]}
    />
  );
}
